
import Cookies from 'js-cookie'
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import TermsAgreement from '@/components/TermsAgreement.vue'
import TheModal from '@/components/TheModal.vue'
import { useSubmitOnce } from '@/composables/useSubmitOnce'
import { TERMS_ACCEPTED_PARAMS } from '@/config/legal'
import { setAuthToken } from '@/helpers/authToken'
import { referralCodeParams } from '@/helpers/referralCode'
import { redirectToCurrentSignupStep } from '@/helpers/signupJourney'
import { signupSteps as steps } from '@/helpers/signupSteps'
import store from '@/store'
import api from '@/store/api'
import { firstStoreObjectOfType, mapRelationships } from '@/store/mappers'

export default defineComponent({
  components: { TermsAgreement, TheModal },
  setup() {
    const refs = ref({})
    const router = useRouter()
    const data = reactive({
      areaCode: '',
      signupError: ''
    })
    const showPasswordModal = ref(false)
    const reenteredPassword = ref('')
    const { inFlight, submitOnce } = useSubmitOnce()

    const onboardingAccount = computed(() => firstStoreObjectOfType('onboarding_account'))
    const onboardingQuestions = computed(() =>
      mapRelationships(onboardingAccount.value?.relationships.onboarding_questions.data)
    )
    const areaCodeQuestion = computed(
      () => onboardingQuestions.value.filter(obj => obj.attributes.target_column === 'preferred_provision_area_code')[0]
    )
    const onboardingAnswers = computed(() =>
      mapRelationships(onboardingAccount.value?.relationships.onboarding_answers.data)
    )
    const areaCodeAnswer = computed(() =>
      onboardingAnswers.value.find(obj => obj.relationships.onboarding_question.data.id === areaCodeQuestion.value.id)
    )

    const emailQuestion = computed(() =>
      onboardingQuestions.value.find(obj => obj.attributes.target_column === 'email')
    )
    const emailAnswer = computed(() =>
      onboardingAnswers.value.find(obj => obj.relationships.onboarding_question.data.id === emailQuestion.value.id)
    )

    watch(data, (currentValue, oldValue) => {
      if (currentValue.areaCode.length > 3) {
        data.areaCode = currentValue.areaCode.slice(0, 3)
      }
    })
    watch(onboardingAccount, (newValue, oldValue) => {
      data.areaCode = areaCodeAnswer.value?.attributes.answer || ''
    })

    const getQuestions = () => {
      api
        .call('GET', 'onboarding?included=onboarding_questions,onboarding_answers', null, {
          authToken: Cookies.get('onboarding_auth_token')
        })
        .then(() => {
          redirectToCurrentSignupStep(router)
        })
    }

    const formSubmitted = () => submitOnce(() => submitAnswer(areaCodeQuestion.value.id))

    const savedValues = () => {
      return JSON.parse(localStorage.getItem('signupPropertyValues'))
    }

    const areaInputContainerClicked = () => {
      refs.value.areaCodeInputRef.focus()
    }

    const totalProgress = () => {
      return onboardingAnswers.value.length
    }

    const getSignupPassword = () => {
      return sessionStorage.getItem('signupPassword')
    }

    const confirmPassword = () => {
      if (reenteredPassword.value) {
        sessionStorage.setItem('signupPassword', reenteredPassword.value)
        showPasswordModal.value = false
        submitOnce(completeSignup)
      }
    }

    const completeSignup = () => {
      data.signupError = ''
      return api
        .call(
          'POST',
          'signup',
          {
            onboarding_account_secret: onboardingToken(),
            password: getSignupPassword(),
            ...TERMS_ACCEPTED_PARAMS,
            ...referralCodeParams()
          },
          false
        )
        .then(response => {
          setAuthToken(response.data.token)
          sessionStorage.removeItem('signupPassword')
          store.dispatch('getLoggedInUser')
          store.dispatch('clearSignUpPropertyValues')
          Cookies.remove('onboarding_auth_token', null)
          router.push('/stripe-integration-required')
        })
        .catch(error => {
          data.signupError =
            error.response?.data?.errors?.[0] || 'Something went wrong creating your account. Please try again.'
        })
    }

    const finishAfterAnswer = () => {
      if (getSignupPassword()) return completeSignup()
      showPasswordModal.value = true
      return undefined
    }

    const submitAnswer = id => {
      fbq('trackCustom', 'OnboardingFlowAnswer', { question: 'AreaCode', answer: data.areaCode })
      data.signupError = ''
      return api
        .call(
          'POST',
          'onboarding?included=onboarding_answers',
          {
            answer: {
              onboarding_answers_attributes: [
                {
                  answer: data.areaCode,
                  onboarding_question_id: areaCodeQuestion.value.id
                }
              ]
            }
          },
          { authToken: Cookies.get('onboarding_auth_token') }
        )
        .then(() => {
          const values = savedValues() || {}
          values['nextPage'] = 6
          return store.dispatch('setSignUpPropertyValues', values).then(finishAfterAnswer)
        })
        .catch(() => {
          data.signupError = 'Something went wrong saving your answer. Please try again.'
        })
    }

    const validateKeyPress = event => {
      if ((event.charCode >= 48 && event.charCode <= 57) || event.charCode == 13) {
        return true
      } else {
        event.preventDefault()
      }
    }

    const onboardingToken = () => {
      return Cookies.get('onboarding_auth_token')
    }

    const populateAnswer = () => {
      if (onboardingToken()) {
        getQuestions()
      }
    }

    populateAnswer()

    return {
      formSubmitted,
      data,
      savedValues,
      areaInputContainerClicked,
      refs,
      steps,
      totalProgress,
      validateKeyPress,
      getQuestions,
      onboardingToken,
      populateAnswer,
      showPasswordModal,
      reenteredPassword,
      confirmPassword,
      inFlight
    }
  }
})
